import { Component, createRef, type ReactNode, type RefObject } from 'react'

type Rects = Map<string, DOMRect>

const reducedMotion = () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches

/**
 * Glides `[data-flip-id]` elements to their new place when `watch` changes
 * (FLIP: measure before the commit, invert, play). A card dropped by drag is in
 * `skip` — it lands where the pointer let go, so it only fades in. Nothing
 * animates under prefers-reduced-motion.
 */
export class FlipGroup extends Component<{ watch: unknown; skip?: RefObject<string | null>; children: ReactNode }> {
  private root = createRef<HTMLDivElement>()

  private measure(): Rects {
    const rects: Rects = new Map()
    this.root.current?.querySelectorAll<HTMLElement>('[data-flip-id]').forEach((el) => rects.set(el.dataset.flipId!, el.getBoundingClientRect()))
    return rects
  }

  getSnapshotBeforeUpdate(prev: { watch: unknown }): Rects | null {
    return prev.watch === this.props.watch || reducedMotion() ? null : this.measure()
  }

  componentDidUpdate(_prev: unknown, _state: unknown, before: Rects | null) {
    if (!before) return
    const skip = this.props.skip?.current ?? null
    if (this.props.skip) this.props.skip.current = null
    this.root.current?.querySelectorAll<HTMLElement>('[data-flip-id]').forEach((el) => {
      const id = el.dataset.flipId!
      const from = before.get(id)
      const to = el.getBoundingClientRect()
      if (id === skip || !from) {
        if (id === skip) el.animate([{ opacity: 0.4 }, { opacity: 1 }], { duration: 180, easing: 'ease-out' })
        return
      }
      const dx = from.left - to.left
      const dy = from.top - to.top
      if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return
      el.animate([{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'none' }], { duration: 220, easing: 'cubic-bezier(0.2, 0, 0, 1)' })
    })
  }

  render() {
    return (
      <div ref={this.root} className="contents">
        {this.props.children}
      </div>
    )
  }
}
